// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { PriceType } from '@shared/utils/History/CashItemUtils';
import { Color } from '../../../Graphics';
import { MAMode } from '../IndicatorConstants';
import { IndicatorScriptBase } from '../IndicatorScriptBase';
import { LineStyle } from '../IndicatorScriptBaseEnums';
import { InputParameterInteger } from '../InputParamaterClasses/InputParameterInteger';
import { InputParameterCombobox } from '../InputParamaterClasses/InputParameterCombobox';
export class ADX extends IndicatorScriptBase {
    constructor() {
        super();
        this.ProjectName = 'Average Directional Index';
        this.Comments = 'Determines the strength of a prevailing trend';
        this.SetIndicatorLine('ADX', Color.Green, 1, LineStyle.SimpleChart);
        this.SetIndicatorLine('+DI', Color.Blue, 1, LineStyle.SimpleChart);
        this.SetIndicatorLine('-DI', Color.Red, 1, LineStyle.SimpleChart);
        this.SeparateWindow = true;
        this.MAType = MAMode.SMA;
        super.InputParameter(new InputParameterCombobox('MAType', 'Type of Moving Average', 0, [
            { Simple: MAMode.SMA },
            { Exponential: MAMode.EMA },
            { Modified: MAMode.SMMA },
            { 'Linear Weighted': MAMode.LWMA }
        ]));
        this.MAPeriod = 14;
        super.InputParameter(new InputParameterInteger('MAPeriod', 'Period of Moving Average', 1, 1, 9999));
    }
    Init() {
        this.IndicatorShortName(this.GetIndicatorShortName());
        this.UpdateIndexDrawBegin();
    }
    UpdateIndexDrawBegin() {
        this.SetIndexDrawBegin(0, 2 * this.MAPeriod - 1);
        this.SetIndexDrawBegin(1, this.MAPeriod);
        this.SetIndexDrawBegin(2, this.MAPeriod);
    }
    GetIndicatorShortName() {
        return 'ADX(' + this.MAPeriod + ')';
    }
    getCustomName() {
        return 'ADX (' + this.ProjectName + ')';
    }
    createInternalIndicators() {
        this.trMa = this.Indicators.iMAExOnArray(this.CurrentData, this.getTrueRange.bind(this), this.MAPeriod, this.MAType, 0, 1);
        this.dmPlusMa = this.Indicators.iMAExOnArray(this.CurrentData, this.getDmPlus.bind(this), this.MAPeriod, this.MAType, 0, 1);
        this.dmMinusMa = this.Indicators.iMAExOnArray(this.CurrentData, this.getDmMinus.bind(this), this.MAPeriod, this.MAType, 0, 1);
        this.adxMa = this.Indicators.iMAExOnArray(this.CurrentData, this.getDX.bind(this), this.MAPeriod, this.MAType, 0, this.MAPeriod);
        return [this.trMa, this.dmPlusMa, this.dmMinusMa, this.adxMa];
    }
    getTrueRange(index) {
        const cd = this.CurrentData;
        const hi = cd.getPriceByType(PriceType.High, index);
        const lo = cd.getPriceByType(PriceType.Low, index);
        const prevClose = cd.Count > index + 1
            ? cd.getPriceByType(PriceType.Close, index + 1)
            : cd.getPriceByType(PriceType.Close, index);
        const tr1 = hi - lo;
        const tr2 = Math.abs(prevClose - hi);
        const tr3 = Math.abs(prevClose - lo);
        return Math.max(tr1, Math.max(tr2, tr3));
    }
    getDmPlus(index) {
        const cd = this.CurrentData;
        if (index + 1 >= cd.Count)
            return 0;
        const upMove = cd.getPriceByType(PriceType.High, index) - cd.getPriceByType(PriceType.High, index + 1);
        const downMove = cd.getPriceByType(PriceType.Low, index + 1) - cd.getPriceByType(PriceType.Low, index);
        return upMove > downMove && upMove > 0 ? upMove : 0;
    }
    getDmMinus(index) {
        const cd = this.CurrentData;
        if (index + 1 >= cd.Count)
            return 0;
        const upMove = cd.getPriceByType(PriceType.High, index) - cd.getPriceByType(PriceType.High, index + 1);
        const downMove = cd.getPriceByType(PriceType.Low, index + 1) - cd.getPriceByType(PriceType.Low, index);
        return downMove > upMove && downMove > 0 ? downMove : 0;
    }
    getDI(index) {
        const smTR = this.trMa.GetValue(index);
        if (!smTR)
            return { plusDI: 0, minusDI: 0 };
        const smPlus = this.dmPlusMa.GetValue(index);
        const smMinus = this.dmMinusMa.GetValue(index);
        const plusDI = (100 * smPlus) / smTR;
        const minusDI = (100 * smMinus) / smTR;
        return { plusDI, minusDI };
    }
    getDX(index) {
        const { plusDI, minusDI } = this.getDI(index);
        const denom = plusDI + minusDI;
        if (denom === 0)
            return 0;
        return (Math.abs(plusDI - minusDI) * 100) / denom;
    }
    OnQuote() {
        super.OnQuote();
        const count = this.CurrentData.Count;
        if (count <= this.MAPeriod) {
            this.SetValue(0, 0, 0);
            this.SetValue(1, 0, 0);
            this.SetValue(2, 0, 0);
            return;
        }
        const { plusDI, minusDI } = this.getDI(0);
        const adx = this.adxMa.GetValue(0);
        if (count < 2 * this.MAPeriod) {
            this.SetValue(0, 0, 0);
        }
        else {
            this.SetValue(0, 0, adx);
        }
        this.SetValue(1, 0, plusDI);
        this.SetValue(2, 0, minusDI);
        // process.stdout.write('==============================================');
        // process.stdout.write(`INDEX: ${this.CurrentData.Count}\n`);
        // process.stdout.write(`DI+ ${plusDI}\n`);
        // process.stdout.write(`DI- ${minusDI}\n`);
        // process.stdout.write(`ADX ${adx}\n`);
    }
}
